import { motion, useReducedMotion } from 'framer-motion';
import { Quote } from 'lucide-react';
import { ABOUT_HERO } from '@/data/about';
import { SlashMark } from '../AmdenMark';

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={reduce ? false : { y: '110%' }}
      animate={reduce ? undefined : { y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const AboutHero = () => {
  const reduce = useReducedMotion();

  return (
    <section
      aria-labelledby="about-heading"
      data-testid="about-hero"
      className="relative overflow-hidden bg-white"
    >
      <SlashMark
        className="pointer-events-none absolute -right-16 top-10 h-[380px] w-[380px] opacity-[0.05]"
        bar="#0B1729"
        dot="#FFB800"
      />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-gold-100/50 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-6">
          <motion.p
            data-testid="about-hero-eyebrow"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-gold-700"
          >
            <span className="h-px w-8 bg-gold-600" aria-hidden="true" />
            {ABOUT_HERO.eyebrow}
          </motion.p>

          <h1
            id="about-heading"
            data-testid="about-hero-heading"
            className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.1} reduce={reduce}>
              {ABOUT_HERO.headingA}
            </MaskedLine>
            <MaskedLine delay={0.22} reduce={reduce}>
              <span className="font-serif font-semibold italic text-gold-700">{ABOUT_HERO.headingAccent}</span>
            </MaskedLine>
          </h1>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
          >
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {ABOUT_HERO.body}
            </p>
            <p className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-bold uppercase tracking-[0.14em] text-navy-800" data-testid="about-hero-service-lines">
              {ABOUT_HERO.serviceLineSummary.map((line, i) => (
                <span key={line} className="flex items-center gap-3">
                  {i > 0 && <span className="h-1 w-1 rounded-full bg-gold-600" aria-hidden="true" />}
                  {line}
                </span>
              ))}
            </p>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-lift">
              <img
                src={ABOUT_HERO.image.src}
                alt={ABOUT_HERO.image.alt}
                width={ABOUT_HERO.image.width}
                height={ABOUT_HERO.image.height}
                fetchPriority="high"
                data-testid="about-hero-image"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <motion.div
              data-testid="about-hero-tagline-card"
              initial={reduce ? false : { opacity: 0, y: 24 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="absolute -bottom-7 left-4 flex max-w-xs items-center gap-3.5 rounded-xl border border-slate-200 bg-white p-5 shadow-lift sm:left-8"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold-100 text-gold-700">
                <Quote className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="text-sm font-bold leading-snug text-navy-900">
                {ABOUT_HERO.taglineCard}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
